import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { CardDisplayService } from "@app/shared/card-display";
import { IColumnData, TaxprepT1Card } from "@app/views-interfaces";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { AnalyticsService, ColumnDataLayoutService, DashboardsService, FiltersService, StepsService } from "@shared/index";
import { CardViewService } from "@shared/card-view-service/card-view.service";
import { HelpersService } from "@shared/helpers-service/helpers.service";
import { DragAndDropService } from "@app/drag-and-drop/drag-and-drop.service";

@Component({
    selector: "wk-active-view",
    templateUrl: "./active-view.component.html",
    styleUrls: ["./active-view.component.scss", "../views-common.scss"],
    standalone: false,
    host: { class: "wk-activeview" },
})
export class ActiveViewComponent implements OnInit, OnDestroy {
    private readonly columnDataLayoutService = inject(ColumnDataLayoutService); // TODO This is injected to perform side-effects, we should refactor and remove
    private readonly analyticsService = inject(AnalyticsService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly cardViewService = inject(CardViewService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly helpersService = inject(HelpersService);
    private readonly stepsService = inject(StepsService);
    private readonly filtersService = inject(FiltersService);
    private readonly dragAndDropService = inject(DragAndDropService);

    private readonly subscription = new Subscription();

    protected displayMode = "Kanban";
    protected columns: IColumnData<TaxprepT1Card>[] = [];
    private rawColumns: IColumnData<TaxprepT1Card>[] = [];

    constructor() {}

    ngOnInit() {
        this.cardViewService.setCurrentViewType("active");
        this.analyticsService.track("Viewed Active View");
        this.init();

        this.subscription.add(
            this.cardViewService.viewDisplayModeChangeEvent$.subscribe(() => {
                this.displayMode = this.cardViewService.currentViewDisplayMode;
            }),
        );
        this.subscription.add(
            this.dashboardsService.currentDashboardChanged$.subscribe(() => {
                // Synchronously invalidate stale column data to avoid filters applied to wrong card types on dashboard switch (DASH-21677)
                this.rawColumns = [];
                this.columns = [];
                this.init();
            }),
        );
        this.subscription.add(
            this.cardDisplayService.cardChangedEvent$.pipe(debounceTime(200), this.dragAndDropService.deferWhileDragging()).subscribe(() => {
                this.initColumns();
            }),
        );
        this.subscription.add(
            this.filtersService.filtersChangedEvent$.pipe(this.dragAndDropService.deferWhileDragging()).subscribe(() => {
                this.applyCardFilters();
            }),
        );
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    async init() {
        await this.filtersService.refreshFiltersDependencies();
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        const key = this.helpersService.getCurrentUser().sub + currentDashboard.Id + currentDashboard.TaxYear + this.cardViewService.currentViewType;
        const recentView = localStorage.getItem(key);
        this.displayMode = recentView ?? "Kanban";
        this.initColumns();
    }

    async initColumns() {
        // This should be generalized for all the views
        const cardModel = await this.cardDisplayService.getModel();
        const steps = await this.stepsService.getStepsList();
        await this.helpersService.mergeSteps(); // TODO return this thing where it came back or so help me
        const mergedSteps = this.helpersService.getMergedSteps(); // TODO return this thing where it came back or so help me
        this.rawColumns =
            steps
                ?.filter((step) => step.State === "active")
                .map((step) => {
                    const statuses = mergedSteps
                        .find((mergedStep) => mergedStep.Id === step.Id)
                        ?.Statuses.filter((status) => status.TaxReturnStatus != null);
                    const stepStatusIds = new Set<string | null | undefined>((statuses ?? []).map((status) => status.TaxReturnStatus.Id));
                    return {
                        id: step.Id,
                        columnName: step.Name,
                        statuses: statuses,
                        state: step.State,
                        isCustomStatus: false,
                        fromCache: false,
                        cards: cardModel
                            .filter((model) => stepStatusIds.has(model.statusId))
                            .flatMap((model) => model.cards as TaxprepT1Card[]),
                    } as IColumnData<TaxprepT1Card>;
                }) ?? [];
        this.applyCardFilters();
    }

    private applyCardFilters(): void {
        this.columns = this.rawColumns.map((column) => {
            const cards = this.filtersService.filterCards(column.cards) as TaxprepT1Card[];
            this.filtersService.sortCards(cards);
            return { ...column, cards };
        });
    }
}
